/**
 * Wagamour Agentic Reasoning Matrix
 * 
 * Maps pet traits, weather, and mobility scores to product recommendations
 * Used for generating personalized insights and badges
 */

export interface PetTraits {
  name?: string
  breed?: string
  ageYears?: number
  ageBracket?: 'puppy' | 'adult' | 'senior' | string
  size?: 'small' | 'medium' | 'large' | 'xlarge' | string
  hasJointIssues?: boolean
  hasBreathingIssues?: boolean
  hasAllergies?: boolean
  energyLevel?: 'low' | 'medium' | 'high' | string
  weatherTolerance?: string[]
}

export interface WeatherContext {
  temperature?: number
  condition?: string
  humidity?: number
}

export interface MobilityScore {
  score: number // 0-100, where 100 = full mobility
  level: 'high' | 'medium' | 'low'
  factors: string[]
}

/**
 * Calculate mobility score based on pet traits and weather
 */
export function calculateMobilityScore(
  traits: PetTraits | null | undefined,
  weather: WeatherContext | null | undefined
): MobilityScore {
  if (!traits) {
    return { score: 100, level: 'high', factors: [] }
  }

  let score = 100
  const factors: string[] = []

  // Age factor
  if (traits.ageBracket === 'senior' || (traits.ageYears && traits.ageYears >= 8)) {
    score -= 25
    factors.push('senior age')
  } else if (traits.ageBracket === 'puppy' || (traits.ageYears && traits.ageYears < 1)) {
    score -= 5 // Puppies have high energy but need supervision
    factors.push('puppy age')
  }

  // Joint issues
  if (traits.hasJointIssues) {
    score -= 30
    factors.push('joint issues')
  }

  // Energy level
  if (traits.energyLevel === 'low') {
    score -= 15
    factors.push('low energy')
  }

  // Breathing issues + high temperature
  const isBrachycephalic = traits.hasBreathingIssues || 
    (traits.breed && ['pug', 'bulldog', 'french bulldog', 'boston terrier', 'boxer', 'shih tzu', 'pekingese'].includes(traits.breed.toLowerCase()))
  
  if (isBrachycephalic && weather?.temperature && weather.temperature > 75) {
    score -= 20
    factors.push('heat sensitivity')
  }

  // Normalize score
  score = Math.max(0, Math.min(100, score))

  // Determine level
  let level: 'high' | 'medium' | 'low'
  if (score >= 70) {
    level = 'high'
  } else if (score >= 40) {
    level = 'medium'
  } else {
    level = 'low'
  }

  return { score, level, factors }
}

/**
 * Generate category-level insight based on traits and weather
 * Returns a 12-word-or-less insight explaining why this category is recommended
 */
export function generateCategoryInsight(
  categoryId: string,
  traits: PetTraits | null | undefined,
  weather: WeatherContext | null | undefined,
  mobilityScore: MobilityScore
): string | null {
  if (!traits || !traits.name) {
    return null // Fail-safe: no insight if no pet profile
  }

  const dogName = traits.name
  const category = categoryId.toLowerCase()

  // Walk Essentials
  if (category.includes('walk') || category.includes('essentials')) {
    if (mobilityScore.level === 'low') {
      return `I've curated these essentials for ${dogName} because gentle support is needed.`
    }
    if (weather?.temperature && weather.temperature > 80) {
      return `I've curated these essentials for ${dogName} because heat safety matters.`
    }
    return `I've curated these essentials for ${dogName} because comfort and safety come first.`
  }

  // Health & Wellness
  if (category.includes('health') || category.includes('wellness')) {
    if (traits.hasJointIssues || mobilityScore.level === 'low') {
      return `I've curated these wellness products for ${dogName} because joint support is essential.`
    }
    if (traits.hasAllergies) {
      return `I've curated these wellness products for ${dogName} because allergy management is key.`
    }
    return `I've curated these wellness products for ${dogName} because proactive care matters.`
  }

  // Toys
  if (category.includes('toy')) {
    if (mobilityScore.level === 'low') {
      return `I've curated these toys for ${dogName} because gentle enrichment is important.`
    }
    if (traits.energyLevel === 'high') {
      return `I've curated these toys for ${dogName} because high energy needs engagement.`
    }
    return `I've curated these toys for ${dogName} because play keeps them happy and healthy.`
  }

  // Food & Nutrition
  if (category.includes('food') || category.includes('nutrition')) {
    if (traits.ageBracket === 'senior' || (traits.ageYears && traits.ageYears >= 8)) {
      return `I've curated these nutrition products for ${dogName} because senior needs are unique.`
    }
    if (traits.hasAllergies) {
      return `I've curated these nutrition products for ${dogName} because dietary sensitivity matters.`
    }
    return `I've curated these nutrition products for ${dogName} because quality nutrition supports health.`
  }

  // Beds & Furniture
  if (category.includes('bed') || category.includes('furniture')) {
    if (traits.hasJointIssues || mobilityScore.level === 'low') {
      return `I've curated these beds for ${dogName} because orthopedic support is essential.`
    }
    return `I've curated these beds for ${dogName} because comfort and rest are important.`
  }

  // Default
  return `I've curated these products for ${dogName} because they match current needs.`
}

/**
 * Generate product-level reasoning badge
 * Returns a short badge text (e.g., "🐾 Senior Approved", "🌡️ Heat Safe")
 */
export function generateReasoningBadge(
  product: {
    category?: string
    subcategory?: string
    reasoning?: string
  },
  traits: PetTraits | null | undefined,
  weather: WeatherContext | null | undefined,
  mobilityScore: MobilityScore
): string | null {
  if (!traits) {
    return null // Fail-safe: no badge if no pet profile
  }

  // Check product reasoning for keywords
  const reasoning = (product.reasoning || '').toLowerCase()
  const category = (product.category || '').toLowerCase()
  const subcategory = (product.subcategory || '').toLowerCase()

  // Senior-specific badges
  if (traits.ageBracket === 'senior' || (traits.ageYears && traits.ageYears >= 8)) {
    if (reasoning.includes('orthopedic') || reasoning.includes('joint') || 
        category.includes('bed') || subcategory.includes('orthopedic')) {
      return '🐾 Senior Approved'
    }
    if (reasoning.includes('senior') || reasoning.includes('elderly')) {
      return '🐾 Senior Approved'
    }
  }

  // Heat safety badges
  const isBrachycephalic = traits.hasBreathingIssues || 
    (traits.breed && ['pug', 'bulldog', 'french bulldog', 'boston terrier', 'boxer', 'shih tzu', 'pekingese'].includes(traits.breed.toLowerCase()))
  
  if (isBrachycephalic && weather?.temperature && weather.temperature > 75) {
    if (reasoning.includes('cool') || reasoning.includes('heat') || 
        reasoning.includes('temperature') || category.includes('cooling')) {
      return '🌡️ Heat Safe'
    }
  }

  // Joint support badges
  if (traits.hasJointIssues || mobilityScore.level === 'low') {
    if (reasoning.includes('joint') || reasoning.includes('orthopedic') || 
        reasoning.includes('support') || category.includes('joint')) {
      return '🦴 Joint Support'
    }
  }

  // Allergy-friendly badges
  if (traits.hasAllergies) {
    if (reasoning.includes('allergy') || reasoning.includes('hypoallergenic') || 
        reasoning.includes('sensitive')) {
      return '🌿 Allergy Friendly'
    }
  }

  // High-energy badges
  if (traits.energyLevel === 'high' && mobilityScore.level === 'high') {
    if (reasoning.includes('active') || reasoning.includes('energy') || 
        category.includes('toy') || subcategory.includes('active')) {
      return '⚡ High Energy'
    }
  }

  // No badge if no match
  return null
}

/**
 * Safety check: Never recommend high-activity toys for low mobility dogs
 */
export function isProductSafeForMobility(
  product: {
    category?: string
    subcategory?: string
    reasoning?: string
  },
  mobilityScore: MobilityScore
): boolean {
  if (mobilityScore.level === 'low') {
    const category = (product.category || '').toLowerCase()
    const subcategory = (product.subcategory || '').toLowerCase()
    const reasoning = (product.reasoning || '').toLowerCase()

    // Block high-activity toys for low mobility
    if (category.includes('toy') || subcategory.includes('toy')) {
      if (reasoning.includes('active') || reasoning.includes('high energy') || 
          reasoning.includes('strenuous') || reasoning.includes('intense')) {
        return false
      }
    }
  }

  return true
}

